import { InfoCircleOutlined } from '@ant-design/icons';
import { Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import AlertError from '../../../../shared/components/alert-error/alert-error';
import Modal from '../../../../shared/components/modal/modal';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useArchiveSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestArchiveModalId } from '../../constants/constants';
import {
  selectSelectedSignRequestIds,
  selectTempSignRequestId,
  setSelectedSignRequestIds,
} from '../../store/sign-requests-slice';
import styles from './sign-request-archive-modal.module.css';

const SignRequestArchiveModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  const [archiveSignRequest, { isLoading }] = useArchiveSignRequestMutation();
  const selectedSignRequests = useAppSelector(selectSelectedSignRequestIds);
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const closeFormModal = () => {
    dispatch(setSelectedSignRequestIds([]));
    dispatch(closeModal(signRequestArchiveModalId));
  };
  const activeSignRequestId = useAppSelector(selectTempSignRequestId);

  const setSignRequestArchiveAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('successMessage.signRequestArchived'),
      }),
    );
  };

  const onArchiveSubmit = () => {
    setIsErrorAlertVisible(false);
    const payload = activeSignRequestId ? [activeSignRequestId] : selectedSignRequests.map(id => id);

    archiveSignRequest({ signRequests: payload })
      .unwrap()
      .then(() => {
        closeFormModal();
        setSignRequestArchiveAlertMessage(signRequestAlertId);
        setSignRequestArchiveAlertMessage(requestPreviewAlertId);
      })
      .catch(() => setIsErrorAlertVisible(true));
  };

  return (
    <Modal
      title={t('signRequestArchiveModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('signScanRequest.action.uploadToServer', { ns: 'common' })}
      onOk={onArchiveSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      {isErrorAlertVisible && <AlertError message={t('validationError.archiveError')} />}
      {t('signRequestArchiveModal.warning')}
      <div>
        <Typography.Text type="success" className={styles.infoIcon}>
          <InfoCircleOutlined />
        </Typography.Text>
        <Typography.Text type="secondary">
          {t('signScanRequest.hint.infoArchiveDuration', { ns: 'common' })}
        </Typography.Text>
      </div>
    </Modal>
  );
};

const SignRequestArchiveModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestArchiveModalId} Component={SignRequestArchiveModalContent} />;
};

export default SignRequestArchiveModal;
